import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { COUNTRIES } from '../data/pricingData';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  companyName: '',
  contactName: '',
  position: '',
  email: '',
  phone: '',
  country: '',
  state: '',
  estimatedUsers: '',
  estimatedOperators: '',
  estimatedEquipment: '',
  requirements: ''
};

// Self-contained "Request a Quote" dialog for the Professional plan.
export const RequestQuoteDialog = ({ open, onOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const selectedCountryData = COUNTRIES.find(c => c.code === form.country);
  const availableStates = selectedCountryData?.states || [];

  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    setForm(prev => ({
      ...prev,
      country: countryCode,
      state: '',
      phone: country?.phoneCode ? `${country.phoneCode} ` : prev.phone
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const countryName = COUNTRIES.find(c => c.code === form.country)?.name || form.country;
      await axios.post(`${API_URL}/quotes/request`, {
        company_name: form.companyName,
        contact_name: form.contactName,
        position: form.position,
        email: form.email,
        phone: form.phone,
        country: countryName,
        state: form.state,
        estimated_users: parseInt(form.estimatedUsers) || 0,
        estimated_operators: parseInt(form.estimatedOperators) || 0,
        estimated_equipment: parseInt(form.estimatedEquipment) || 0,
        requirements: form.requirements
      });
      toast.success('Quote request submitted!', {
        description: "We'll be in touch within 1-2 business days."
      });
      onOpenChange(false);
      setForm(EMPTY_FORM);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Request a Quote</DialogTitle>
          <DialogDescription>
            Tell us about your business and we'll create a custom Professional plan quote for you.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quote-company">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="quote-company"
                data-testid="quote-company"
                value={form.companyName}
                onChange={(e) => setForm(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="Your company name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-contact">Contact Name <span className="text-red-500">*</span></Label>
              <Input
                id="quote-contact"
                data-testid="quote-contact"
                value={form.contactName}
                onChange={(e) => setForm(prev => ({ ...prev, contactName: e.target.value }))}
                placeholder="Your name"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quote-position">Position / Role</Label>
              <Input
                id="quote-position"
                value={form.position}
                onChange={(e) => setForm(prev => ({ ...prev, position: e.target.value }))}
                placeholder="e.g., Operations Manager"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="quote-email"
                type="email"
                data-testid="quote-email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quote-country">Country <span className="text-red-500">*</span></Label>
              <Select value={form.country} onValueChange={handleCountryChange} required>
                <SelectTrigger id="quote-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-state">State / Province {availableStates.length > 0 ? '*' : ''}</Label>
              <Select
                value={form.state}
                onValueChange={(value) => setForm(prev => ({ ...prev, state: value }))}
                disabled={availableStates.length === 0}
              >
                <SelectTrigger id="quote-state">
                  <SelectValue placeholder={availableStates.length === 0 ? "Select country first" : "Select state/province"} />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.map(state => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quote-phone">Phone</Label>
            <Input
              id="quote-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              placeholder={selectedCountryData?.phoneCode ? `${selectedCountryData.phoneCode} XXX XXX XXX` : "Select country first"}
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <p className="text-sm text-muted-foreground mb-3">Estimated business size</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quote-users">Users <span className="text-red-500">*</span></Label>
                <Input
                  id="quote-users"
                  type="number"
                  min="1"
                  value={form.estimatedUsers}
                  onChange={(e) => setForm(prev => ({ ...prev, estimatedUsers: e.target.value }))}
                  placeholder="5"
                  required
                />
                <p className="text-xs text-muted-foreground">Admin/Dispatchers</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-operators">Operators <span className="text-red-500">*</span></Label>
                <Input
                  id="quote-operators"
                  type="number"
                  min="0"
                  value={form.estimatedOperators}
                  onChange={(e) => setForm(prev => ({ ...prev, estimatedOperators: e.target.value }))}
                  placeholder="15"
                  required
                />
                <p className="text-xs text-muted-foreground">Field workers</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-equipment">Assets <span className="text-red-500">*</span></Label>
                <Input
                  id="quote-equipment"
                  type="number"
                  min="0"
                  value={form.estimatedEquipment}
                  onChange={(e) => setForm(prev => ({ ...prev, estimatedEquipment: e.target.value }))}
                  placeholder="25"
                  required
                />
                <p className="text-xs text-muted-foreground">Fleet size</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quote-requirements">Specific Requirements</Label>
            <Textarea
              id="quote-requirements"
              value={form.requirements}
              onChange={(e) => setForm(prev => ({ ...prev, requirements: e.target.value }))}
              placeholder="Tell us about any specific features or integrations you need..."
              rows={3}
            />
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="btn-volt" disabled={submitting} data-testid="quote-submit">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestQuoteDialog;
